'use client';

import { createContext, useContext } from 'react';
import { CartContext } from '@/entities/cart';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { ModalProps } from '@/shared/ui/modal/types';
import { useVenueSelectFilters } from '../../hooks/useVenueSelectFilters';
import { Props } from './types';

type SelectVenueModalContextValue = ReturnType<typeof useVenueSelectFilters> & {
    isImageVisible?: boolean;
    areSearchFieldsVisible?: boolean;
    isSignInVisible?: boolean;
    onClose: ModalProps['onClose'];
    availableForItemId?: string;
    allowSelectCurrent?: boolean;
    isDeliveryAvailable: boolean;
    isPickupAvailable: boolean;
};

export const SelectVenueModalContext = createContext<SelectVenueModalContextValue>(
    {} as SelectVenueModalContextValue,
);

export const SelectVenueModalProvider = ({
    children,
    isImageVisible = true,
    areSearchFieldsVisible = true,
    isSignInVisible = true,
    availableForItemId,
    allowSelectCurrent,
    ...props
}: Props) => {
    const { formMethods: cartFormMethods } = useContext(CartContext);

    const cartAddress = cartFormMethods.getValues('deliveryMethod.address');
    const cartAddressId = cartFormMethods.getValues('deliveryMethod.addressId');

    const {
        formMethods,
        handleChangeAddress,
        handleChangeDeliveryType,
        isDeliveryAvailable,
        isPickupAvailable,
    } = useVenueSelectFilters({
        initialDeliveryType: cartFormMethods.getValues('deliveryMethod.type') ?? DeliveryType.DELIVERY,
        initialAddress: cartAddress ? { id: cartAddressId ?? undefined, ...cartAddress } : null,
        initialIsManual: cartFormMethods.getValues('deliveryMethod.manual'),
        showAllDeliveryVenuesWithoutDeliverySelected: true,
    });

    return (
        <SelectVenueModalContext.Provider
            value={{
                formMethods,
                handleChangeAddress,
                handleChangeDeliveryType,
                isDeliveryAvailable,
                isPickupAvailable,
                isImageVisible,
                areSearchFieldsVisible,
                isSignInVisible,
                onClose: props.onClose,
                availableForItemId,
                allowSelectCurrent,
            }}
        >
            {children}
        </SelectVenueModalContext.Provider>
    );
};
